import { useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Viewer } from './Viewer.tsx';
import { TopHeader } from './TopHeader.tsx';
import { InfoBar } from './InfoBar.tsx';
import { ScriptPanel } from './ScriptPanel.tsx';
import { ShotLane } from './ShotLane.tsx';
import { AudioLane } from './AudioLane.tsx';
import { Transport } from './Transport.tsx';
import { CommentPanel } from './CommentPanel.tsx';
import { FullscreenTransport } from './FullscreenTransport.tsx';
import { FullscreenViewer } from './FullscreenViewer.tsx';
import { ToastContainer } from './Toast.tsx';
import { KeyboardShortcutsOverlay } from './KeyboardShortcutsOverlay.tsx';
import { HelpDialog } from './HelpDialog.tsx';
import { AiHelpDialog } from './AiHelpDialog.tsx';
import { AiGutterButton } from './AiGutterButton.tsx';
import { AudioGutterButton } from './AudioGutterButton.tsx';
import { AiWorkspace } from './AiWorkspace/AiWorkspace.tsx';
import { AudioWorkspace } from './AudioWorkspace/AudioWorkspace.tsx';
import { PortraitBlocker } from './PortraitBlocker.tsx';
import { useMobileLayout } from '@/hooks/useMobileLayout.ts';
import { usePlayback } from '@/hooks/usePlayback.ts';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts.ts';
import { useAudioPlayback } from '@/hooks/useAudioPlayback.ts';
import { useReviewSync } from '@/hooks/useReviewSync.ts';
import { useGlowTutorial } from '@/hooks/useGlowTutorial.ts';
import { CREATOR_STEPS, REVIEWER_STEPS, markSeenOrient, markSeenOrientReview, buildBackToProjectsStep } from '@/hooks/useQuickOrient.ts';
import { markTimelineMounted, markTimelineDismissed } from '@/telemetry/timelineLifecycle.ts';
import { trackEvent } from '@/telemetry/trackEvent';
import type { CoachStep } from '@/components/CoachMark.tsx';
import { CoachMark } from './CoachMark.tsx';
import { TeaseOverlay } from './TeaseOverlay.tsx';
import { capabilities } from '@/capabilities.ts';
import { useProjectStore } from '@/store/projectStore.ts';
import { useAudioStore } from '@/store/audioStore.ts';
import { useUiStore } from '@/store/uiStore.ts';
// playbackStore is no longer imported here (20260922): its one reader was the deferred tutorial
// autoplay that Quick Orient's dismissal used to fire — see the note beside handleOrientAdvance.
import styles from './TimelineView.module.css';

export function TimelineView() {
  const { t } = useTranslation('timeline');
  const { t: tHelp } = useTranslation('help');
  usePlayback();
  useKeyboardShortcuts();
  useAudioPlayback();
  useGlowTutorial();

  const audioClips = useAudioStore(s => s.audioClips);
  const isFullscreen = useUiStore(s => s.isFullscreen);
  const setFullscreen = useUiStore(s => s.setFullscreen);
  const readOnly = useProjectStore(s => s.readOnly);
  const sessionId = useProjectStore(s => s.sessionId);
  const reviewSessionId = useProjectStore(s => s.reviewSessionId);
  const scriptPanelOpen = useUiStore(s => s.scriptPanelOpen);
  const aiWorkspaceOpen = useUiStore(s => s.aiWorkspaceOpen);
  const audioWorkspaceOpen = useUiStore(s => s.audioWorkspaceOpen);
  const isMobile = useMobileLayout();
  const hideTimeline = aiWorkspaceOpen;
  const isRecording = useUiStore(s => s.isRecording);
  const stopRecordingFn = useUiStore(s => s.stopRecordingFn);
  const orientStep = useUiStore(s => s.orientStep);
  const setOrientStep = useUiStore(s => s.setOrientStep);

  // Determine the session ID to use for realtime sync
  const syncSessionId = sessionId ?? reviewSessionId ?? null;

  // Activate realtime sync when in review mode or embedded creator comments
  useReviewSync(syncSessionId);

  // Fetch existing comments/approvals when embedded mode has a review session.
  // useReviewSync also fetches, but may be delayed waiting for native JWT.
  // _patchRemoteComment deduplicates by content so double-fetching is safe.
  useEffect(() => {
    if (!capabilities.bridge || !reviewSessionId) return;
    import('@/api/reviewApi.ts').then(async ({ getSessionComments, getSessionApprovals }) => {
      const { useCommentStore } = await import('@/store/commentStore.ts');
      const { uuidToNegId } = await import('@/previs/commentId.ts');
      const [comments, approvals] = await Promise.all([
        getSessionComments(reviewSessionId),
        getSessionApprovals(reviewSessionId),
      ]);
      const cs = useCommentStore.getState();
      for (const row of comments) {
        cs._patchRemoteComment({
          id: uuidToNegId(row.id),
          shotId: row.shot_id,
          timecode: row.timecode,
          author: row.author,
          text: row.text,
          createdAt: row.created_at,
          resolvedAt: row.resolved_at,
          parentCommentId: null,
          annotationSvg: row.annotation_svg ?? null,
        });
      }
      for (const row of approvals) {
        const { normalizeApprovalStatus } = await import('@/types/timeline.ts');
        cs._patchRemoteApproval({
          id: uuidToNegId(row.id),
          shotId: row.shot_id,
          status: normalizeApprovalStatus(row.status),
          approvedBy: row.approved_by,
          approvedAt: row.approved_at ?? new Date().toISOString(),
          notes: row.notes,
          revision: row.revision,
        });
      }
    }).catch(err => console.warn('[embeddedComments] Failed to fetch initial comments:', err));
  }, [reviewSessionId]);

  const viewerWrapperRef = useRef<HTMLDivElement>(null);

  // Telemetry: fire timeline_view_mounted once the project actually loads
  // (so project_source from setSqlitePath has landed and rides on the event).
  // In standalone mode the project is loaded synchronously before TimelineView
  // mounts, so this fires on first render. In embedded mode setSqlitePath is
  // async and the project lands a bit after mount, so we wait. Dismissal
  // pairs with this via the unmount cleanup below — and is also dedup-fired
  // from the bridge's __requestBack handler since Unity may tear down the
  // WebView before React's cleanup runs.
  const project = useProjectStore(s => s.project);
  const hasFiredMountRef = useRef(false);
  useEffect(() => {
    if (project && !hasFiredMountRef.current) {
      hasFiredMountRef.current = true;
      markTimelineMounted(readOnly ? 'reviewer' : 'creator');
    }
  }, [project, readOnly]);
  useEffect(() => {
    return () => { markTimelineDismissed(); };
  }, []);

  // Sync fullscreen state with the Fullscreen API
  const onFullscreenChange = useCallback(() => {
    const isFull = !!(document.fullscreenElement ?? (document as unknown as { webkitFullscreenElement?: Element }).webkitFullscreenElement);
    setFullscreen(isFull);
  }, [setFullscreen]);

  useEffect(() => {
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
    };
  }, [onFullscreenChange]);

  // iOS safe areas on the BARE embedded Timeline route (?embedded=1 with no page param) — the
  // shell-less TimelineView some embedded hosts and MCP's ShowTimeline still land on. Mirror of
  // SceneBoardPage's effect, and idempotent with index.html's pre-paint seed for the same reason:
  // that block already turned cover on before first layout, so this only matters if it could not
  // run. Gated on !sceneBoardShell because on the board route SceneBoardPage owns the viewport
  // string — running both would have this cleanup restore the pre-shell value on tab switches.
  // The native side extends this route's frame with SetContentInsetAdjustmentBehavior(Never), so
  // cover here is the only thing making env(safe-area-inset-*) non-zero on either side.
  useEffect(() => {
    if (!capabilities.bridge || capabilities.sceneBoardShell) return;
    const meta = document.querySelector('meta[name="viewport"]');
    if (!meta) return;
    const prev = meta.getAttribute('content');
    meta.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover');
    return () => { if (prev) meta.setAttribute('content', prev); };
  }, []);

  // Show portrait blocker on mobile for web review/team modes (not embedded)
  const showPortraitBlocker = readOnly && !capabilities.bridge;

  // Whether the shell's Home button is currently shed. SceneBoardPage publishes it from a
  // MEASUREMENT of its own top bar (SceneBoard/shellBarFit.ts) and this is a plain subscription,
  // so a resize or rotation mid-tour retargets the step for free. It replaced a matchMedia read
  // against a `(max-width: 600px)` constant that had to be kept in step with a CSS breakpoint by
  // hand; there is no breakpoint left to mirror. False whenever no shell is mounted (the bare
  // embedded Timeline route, standalone web, jsdom) — the same default the matchMedia read had.
  const homeHidden = useUiStore(s => s.shellHomeShed);

  // Quick Orient coach marks. The "Back to Projects" step only applies in embedded
  // creator mode -- standalone web has no shell chrome to point at. Built via
  // buildBackToProjectsStep (useQuickOrient.ts) so the title/text come from i18next (help
  // namespace) and the target follows homeHidden: the shell's Home button
  // (data-orient="projects-home" on SceneBoardPage.tsx) when it's visible, else the hamburger
  // menu's Close Project item on bars too narrow to hold it.
  const backToScenesStep: CoachStep = buildBackToProjectsStep(homeHidden, tHelp);
  // Also requires capabilities.sceneBoardShell: the target lives in the scene-board shell
  // (SceneBoardPage's Home button/hamburger), which isn't mounted on the bare TimelineView route
  // some embedded hosts and MCP's ShowTimeline can still reach — appending the step there would
  // point at an element that doesn't exist on screen.
  const orientSteps = readOnly
    ? REVIEWER_STEPS
    : (capabilities.bridge && capabilities.sceneBoardShell ? [...CREATOR_STEPS, backToScenesStep] : CREATOR_STEPS);
  const orientMode: 'creator' | 'reviewer' = readOnly ? 'reviewer' : 'creator';

  // Telemetry: fire tutorial_step_shown on every step transition (including
  // the very first step). Tracked separately from advance/skip so we can
  // measure step-level reach (denominator) vs. drop-off (numerator). Refs
  // capture the steps array to avoid re-firing when unrelated re-renders
  // happen — only orientStep changing should fire a new event.
  useEffect(() => {
    if (orientStep === null) return;
    const step = orientSteps[orientStep];
    if (!step) return;
    trackEvent('tutorial_step_shown', {
      step_id: step.target,
      step_index: orientStep,
      step_total: orientSteps.length,
      mode: orientMode,
    });
    // Intentionally not depending on orientSteps/orientMode — those don't
    // change once mounted, and including them would spuriously refire on
    // every render. orientStep is the only meaningful trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orientStep]);

  // 20260922 — a `consumePendingTutorialAutoplay` helper used to hang off both of the places
  // orientStep reaches null below, starting the playback that TutorialRail's "Play your scene" had
  // deferred rather than run underneath the coach marks. Both are gone: the marks cannot arm inside
  // a tutorial project (owner ruling, orientAutoArmMode), so "Play your scene" — which only exists
  // there — starts playback itself and nothing is ever deferred to a dismissal here.

  const handleOrientAdvance = useCallback(() => {
    if (orientStep === null) return;
    const step = orientSteps[orientStep];
    if (step) {
      trackEvent('tutorial_step_advanced', {
        step_id: step.target,
        step_index: orientStep,
        step_total: orientSteps.length,
        mode: orientMode,
      });
    }
    const next = orientStep + 1;
    if (next >= orientSteps.length) {
      // Past the last step — full-completion event (separate from the
      // per-step advance) so the funnel can show "started" vs "finished".
      trackEvent('tutorial_completed', {
        step_total: orientSteps.length,
        mode: orientMode,
      });
      if (readOnly) { markSeenOrientReview(); } else { markSeenOrient(); }
      setOrientStep(null);
    } else {
      setOrientStep(next);
    }
  }, [orientStep, orientSteps, orientMode, readOnly, setOrientStep]);

  const handleOrientSkip = useCallback(() => {
    // at_step_index records WHERE in the sequence the user bailed — distinct
    // from tutorial_completed's step_total (final reached step vs. total).
    trackEvent('tutorial_skipped', {
      at_step_index: orientStep ?? 0,
      step_total: orientSteps.length,
      mode: orientMode,
    });
    if (readOnly) { markSeenOrientReview(); } else { markSeenOrient(); }
    setOrientStep(null);
  }, [orientStep, orientSteps.length, orientMode, readOnly, setOrientStep]);


  return (
    <div className={`${styles.container} ${capabilities.sceneBoardShell ? '' : styles.containerUnshelled}`}>
      {showPortraitBlocker && <PortraitBlocker />}
      {!isFullscreen && !hideTimeline && !(isMobile && audioWorkspaceOpen) && <TopHeader />}
      <div
        className={`${styles.viewerWrapper} ${isFullscreen ? styles.viewerFullscreen : ''}`}
        ref={viewerWrapperRef}
        data-viewer-wrapper
      >
        {aiWorkspaceOpen ? (
          <AiWorkspace />
        ) : audioWorkspaceOpen ? (
          <AudioWorkspace />
        ) : (
          <>
            {!isFullscreen && scriptPanelOpen && <ScriptPanel />}
            <Viewer />
            {isFullscreen && <FullscreenViewer />}
            {!isFullscreen && capabilities.comments && <CommentPanel />}
            {isFullscreen && <FullscreenTransport />}
            {isRecording && stopRecordingFn && (
              <button className={styles.recStopOverlay} onClick={stopRecordingFn} title={t('stopRecording')}>
                &#x23F9;
              </button>
            )}
          </>
        )}
      </div>
      {!isFullscreen && !hideTimeline && <InfoBar />}
      {!hideTimeline && (
        <div className={styles.shotLaneWrapper} data-orient="shot-lane">
          <div className={styles.gutterButtons} data-orient="gutter-buttons">
            {capabilities.aiGeneration && <AiGutterButton />}
            <AudioGutterButton />
          </div>
          <ShotLane />
        </div>
      )}
      {!hideTimeline && (!readOnly || audioClips.length > 0) && <AudioLane />}
      <Transport />
      <ToastContainer />
      <KeyboardShortcutsOverlay />
      <HelpDialog />
      <AiHelpDialog />
      <TeaseOverlay />
      {/* Never over the AI workspace (20260904): it hides the timeline (hideTimeline above), so
          every orient target is unmounted while it is open. Transport's auto-arm already refuses
          to START under it; this is the belt for an orientStep set some other way (the "T"
          replay, a workspace opened mid-tour) — the step is kept, and the marks come back the
          moment the workspace closes onto the Timeline. */}
      {orientStep !== null && !aiWorkspaceOpen && (
        <CoachMark
          steps={orientSteps}
          currentStep={orientStep}
          onAdvance={handleOrientAdvance}
          onSkip={handleOrientSkip}
        />
      )}
    </div>
  );
}
